import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../../../services/ApiService';
import ConfirmationModal from '../../../../../../components/ModalConfirmation';
import { extractErrorMessage, plural } from '../constants';
import NovoNcmSelect from './NovoNcmSelect';
import {
  NcmProfileItemDto,
  NcmVinculoCandidato,
  NcmVinculoPrevia,
  NcmVinculoResumo,
  NcmVinculosResponse,
  NcmVinculoTipo,
  NcmVinculoTrocaPayload,
} from '../types';
import {
  CandidateRow,
  Empty,
  Inline,
  InlineHint,
  LinkName,
  LinkRow,
  ListHead,
  MiniButton,
  Muted,
  PushRightButton,
  RowButtons,
  Segmented,
  SegmentedButton,
  Summary,
  SummaryOrders,
  SummaryWide,
} from './style';

interface Props {
  item: NcmProfileItemDto;
  adotados: { ncmId: number; ncm: string }[];
  onChanged: () => void;
  onFeedback: (message: string, ok: boolean) => void;
}

const TIPOS: { tipo: NcmVinculoTipo; label: string }[] = [
  { tipo: 'PRODUTO', label: 'Produtos finais' },
  { tipo: 'RECURSO', label: 'Insumos' },
  { tipo: 'ESTOQUE', label: 'Estoque' },
];

interface LinhaVinculo {
  id: number;
  nome: string;
  origem: string;
  codigo: string | null;
}

interface Confirmacao {
  titulo: string;
  mensagem: string;
  acao: string;
  perigo: boolean;
  payload: NcmVinculoTrocaPayload;
}

const mensagemPrevia = (p: NcmVinculoPrevia, destino: string | null): string => {
  const partes: string[] = [];
  if (p.itensPedidoAfetados > 0) {
    const um = p.itensPedidoAfetados === 1 && p.rascunhosAfetados === 0;
    const alvo = destino
      ? `${um ? 'passa' : 'passam'} para ${destino}`
      : `${um ? 'fica' : 'ficam'} sem NCM e ${um ? 'sai' : 'saem'} da lista de itens da NF-e até ganhar NCM de novo`;
    const rascunhos = p.rascunhosAfetados > 0 ? ` e ${plural(p.rascunhosAfetados, 'rascunho de nota', 'rascunhos de nota')}` : '';
    partes.push(
      `${plural(p.itensPedidoAfetados, 'item', 'itens')} de ${plural(p.pedidosAfetados, 'pedido', 'pedidos')} sem nota emitida${rascunhos} ${alvo}.`,
    );
  } else {
    partes.push('Nenhum pedido sem nota emitida é afetado.');
  }
  if (p.itensPedidoTravados > 0) {
    partes.push(
      `${plural(p.itensPedidoTravados, 'item de pedido', 'itens de pedido')} com nota emitida ${p.itensPedidoTravados === 1 ? 'fica' : 'ficam'} com o NCM da nota.`,
    );
  }
  partes.push(
    `Itens de pedido: ${p.itensPedidoAfetados} · Pedidos sem nota: ${p.pedidosAfetados} · Rascunhos: ${p.rascunhosAfetados} · Travados: ${p.itensPedidoTravados}.`,
  );
  return partes.join(' ');
};

// Abre no primeiro tipo que tem uso, para o clique em "2 recursos" não cair numa lista vazia.
const tipoInicial = (i: NcmProfileItemDto): NcmVinculoTipo =>
  i.usos.produtos > 0 ? 'PRODUTO' : i.usos.resources > 0 ? 'RECURSO' : i.usos.estoques > 0 ? 'ESTOQUE' : 'PRODUTO';

// A tela monta este componente com key = ncmId: trocar de NCM zera o estado.
export default function LinksTab({ item, adotados, onChanged, onFeedback }: Props): React.ReactElement {
  const [vinculos, setVinculos] = useState<NcmVinculosResponse | null>(null);
  const [tipo, setTipo] = useState<NcmVinculoTipo>(() => tipoInicial(item));
  const [resumo, setResumo] = useState<{ id: number; dados: NcmVinculoResumo | null } | null>(null);
  const [troca, setTroca] = useState<{ id: number } | null>(null);
  const [vincularAberto, setVincularAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [candidatos, setCandidatos] = useState<NcmVinculoCandidato[]>([]);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let ativo = true;
    ApiService.fiscal
      .getNcmVinculos(item.ncmId)
      .then((r) => {
        if (ativo) setVinculos(r.data);
      })
      .catch((error: unknown) => onFeedback(extractErrorMessage(error, 'Erro ao buscar os vínculos.'), false));
    return () => {
      ativo = false;
    };
  }, [item.ncmId, versao, onFeedback]);

  useEffect(() => {
    setResumo(null);
    setTroca(null);
    setVincularAberto(false);
    setBusca('');
  }, [tipo]);

  useEffect(() => {
    if (!vincularAberto) {
      setCandidatos([]);
      return;
    }
    let ativo = true;
    const timer = setTimeout(() => {
      ApiService.fiscal
        .getNcmVinculoCandidatos({ tipo, ncmId: item.ncmId, search: busca.trim() || undefined })
        .then((r) => {
          if (ativo) setCandidatos(r.data?.itens ?? []);
        })
        .catch((error: unknown) => console.error('Erro ao buscar candidatos:', error));
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [vincularAberto, busca, tipo, item.ncmId, versao]);

  const linhas = useMemo<Record<NcmVinculoTipo, LinhaVinculo[]>>(
    () => ({
      PRODUTO: (vinculos?.produtos ?? []).map((p) => ({ id: p.id, nome: p.nome, origem: p.nome || p.codigo || `#${p.id}`, codigo: p.codigo })),
      RECURSO: (vinculos?.recursos ?? []).map((r) => ({ id: r.id, nome: r.nome, origem: r.nome || r.codigo || `#${r.id}`, codigo: r.codigo })),
      ESTOQUE: (vinculos?.estoques ?? []).map((e) => {
        const nome = e.descricao || e.codigo || `#${e.id}`;
        return { id: e.id, nome, origem: nome, codigo: e.codigo };
      }),
    }),
    [vinculos],
  );

  const abrirResumo = async (id: number) => {
    if (resumo?.id === id) return setResumo(null);
    setResumo({ id, dados: null });
    try {
      const r = await ApiService.fiscal.getNcmVinculoResumo(tipo, id);
      setResumo((atual) => (atual?.id === id ? { id, dados: r.data } : atual));
    } catch (error: unknown) {
      setResumo(null);
      onFeedback(extractErrorMessage(error, 'Erro ao buscar o resumo.'), false);
    }
  };

  const pedirConfirmacao = useCallback(
    async (payload: NcmVinculoTrocaPayload, titulo: string, acao: string, perigo: boolean, destino: string | null) => {
      try {
        const r = await ApiService.fiscal.previaNcmVinculo(payload);
        setConfirmacao({ titulo, mensagem: mensagemPrevia(r.data, destino), acao, perigo, payload });
      } catch (error: unknown) {
        onFeedback(extractErrorMessage(error, 'Erro ao calcular o efeito da troca.'), false);
      }
    },
    [onFeedback],
  );

  const confirmar = async () => {
    if (!confirmacao) return;
    const { payload } = confirmacao;
    setConfirmacao(null);
    try {
      const r = await ApiService.fiscal.trocarNcmVinculo(payload);
      onFeedback(
        `${plural(r.data.itensAtualizados, 'item atualizado', 'itens atualizados')} · ${plural(r.data.itensPedidoAtualizados, 'item de pedido', 'itens de pedido')} · ${plural(r.data.rascunhosAtualizados, 'rascunho', 'rascunhos')}.`,
        true,
      );
      setTroca(null);
      setVincularAberto(false);
      setResumo(null);
      setVersao((v) => v + 1);
      onChanged();
    } catch (error: unknown) {
      onFeedback(extractErrorMessage(error, 'Erro ao gravar a troca de NCM.'), false);
    }
  };

  const outrosNcms = adotados.filter((a) => a.ncmId !== item.ncmId);
  const lista = linhas[tipo];
  const tipoLabel = TIPOS.find((t) => t.tipo === tipo)?.label ?? '';

  return (
    <>
      <Segmented>
        {TIPOS.map((t) => (
          <SegmentedButton key={t.tipo} type="button" $active={t.tipo === tipo} onClick={() => setTipo(t.tipo)}>
            {t.label} {linhas[t.tipo].length}
          </SegmentedButton>
        ))}
      </Segmented>

      <ListHead>
        {tipoLabel}
        <PushRightButton type="button" onClick={() => setVincularAberto((v) => !v)}>
          + Vincular
        </PushRightButton>
      </ListHead>

      {vincularAberto && (
        <Inline>
          <input placeholder="Buscar por nome ou código" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <InlineHint>Primeiro os itens sem NCM, depois os que usam outro NCM.</InlineHint>
          {candidatos.map((c) => (
            <CandidateRow key={c.id}>
              <LinkName as="div">
                <b>{c.nome ?? c.codigo ?? `#${c.id}`}</b>
                <span>
                  {c.codigo ?? '—'} · {c.ncmAtual ? `hoje em ${c.ncmAtual}` : 'sem NCM'}
                </span>
              </LinkName>
              <MiniButton
                type="button"
                onClick={() =>
                  pedirConfirmacao(
                    { tipo, ids: [c.id], ncmId: item.ncmId },
                    `Vincular ${c.nome ?? c.codigo ?? ''} a ${item.ncm}?`,
                    'Vincular',
                    false,
                    item.ncm,
                  )
                }
              >
                Vincular
              </MiniButton>
            </CandidateRow>
          ))}
        </Inline>
      )}

      <div>
        {vinculos && lista.length === 0 && <Empty>Nenhum item usa este NCM.</Empty>}
        {lista.map((l) => (
          <LinkRow key={l.id}>
            <LinkName type="button" onClick={() => abrirResumo(l.id)}>
              <b>{l.origem}</b>
              <span>
                {l.codigo ?? '—'} · {resumo?.id === l.id ? 'fechar resumo' : 'ver resumo'}
              </span>
            </LinkName>
            <RowButtons>
              <MiniButton
                type="button"
                onClick={() => setTroca(troca?.id === l.id ? null : { id: l.id })}
              >
                Trocar NCM
              </MiniButton>
              <MiniButton
                type="button"
                $danger
                onClick={() =>
                  pedirConfirmacao({ tipo, ids: [l.id], ncmId: null }, `Desvincular ${l.origem}?`, 'Desvincular', true, null)
                }
              >
                Desvincular
              </MiniButton>
            </RowButtons>

            {resumo?.id === l.id && <ResumoView dados={resumo.dados} />}

            {troca?.id === l.id && (
              <Inline>
                <NovoNcmSelect
                  adotados={outrosNcms}
                  onEscolher={(destino) =>
                    pedirConfirmacao(
                      { tipo, ids: [l.id], ncmId: destino.ncmId },
                      `Trocar o NCM de ${l.origem}?`,
                      'Trocar NCM',
                      false,
                      destino.ncm,
                    )
                  }
                />
              </Inline>
            )}
          </LinkRow>
        ))}
      </div>

      <ConfirmationModal
        isOpen={!!confirmacao}
        title={confirmacao?.titulo ?? ''}
        message={confirmacao?.mensagem ?? ''}
        actions={[
          { label: 'Cancelar', onClick: () => setConfirmacao(null), variant: 'outline' },
          {
            label: confirmacao?.acao ?? 'Confirmar',
            onClick: confirmar,
            variant: confirmacao?.perigo ? 'danger' : 'primary',
          },
        ]}
      />
    </>
  );
}

function ResumoView({ dados }: { dados: NcmVinculoResumo | null }): React.ReactElement {
  if (!dados) return <Summary><Muted>Carregando…</Muted></Summary>;
  const campo = (rotulo: string, valor: string | null) => (
    <div>
      <dt>{rotulo}</dt>
      <dd>{valor || '—'}</dd>
    </div>
  );
  if (dados.tipo === 'PRODUTO') {
    return (
      <Summary>
        {campo('Código', dados.codigo)}
        {campo('Norma', dados.norma)}
        {campo('Formato', dados.formato)}
        {campo('Unidade', dados.unidade)}
        {campo('NCM', dados.ncm)}
        <SummaryOrders>
          <dt>Últimos pedidos</dt>
          {dados.ultimosPedidos.length === 0 && <Muted>Nenhum pedido.</Muted>}
          {dados.ultimosPedidos.map((p) => (
            <div key={p.orderItemId}>
              <span>
                #{p.orderId} · {p.cliente ?? '—'}
              </span>
              <Muted>
                {p.data.split('-').reverse().join('/')} · {p.quantidade}
              </Muted>
            </div>
          ))}
        </SummaryOrders>
      </Summary>
    );
  }
  if (dados.tipo === 'RECURSO') {
    return (
      <Summary>
        {campo('Código', dados.codigo)}
        {campo('Nome', dados.nome)}
        {campo('NCM', dados.ncm)}
        <SummaryWide>
          <dt>Descrição</dt>
          <dd>{dados.descricao || '—'}</dd>
        </SummaryWide>
      </Summary>
    );
  }
  return (
    <Summary>
      {campo('Código', dados.codigo)}
      {campo('Material', dados.material)}
      {campo('Corrida', dados.corrida)}
      {campo('Local', dados.local)}
      {campo('NCM', dados.ncm)}
      <SummaryWide>
        <dt>Descrição</dt>
        <dd>{dados.descricao || '—'}</dd>
      </SummaryWide>
    </Summary>
  );
}
